// **React Imports
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

// **MUI Imports
import { ChevronLeft } from '@mui/icons-material'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'

// **Context Imports
import { SettingsContext } from '../../../../context/setting'

// **Style Imports
import '../../../../components/sidepanel/index.scss'

// **Logo Imports
import DefaultLogo from '../../../../assets/images/logo-dark.svg'
import DefaultCombinedLogo from '../../../../assets/images/logo-with-wordmark-dark.svg'
import { candidateItemList } from '../../../../components/sidepanel/sidepanelItems'

// drawer width
const drawerWidth = 240

// Custom MUI styles
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 35px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 35px)`
  }
})

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.primary.main,
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}))

// Separate Image component
const ImageContainer = memo(({ expanded }) => {
  // ** variables
  const { settings } = useContext(SettingsContext)

  const tenantCombinedLogo = useMemo(() => {
    return settings?.tenantConfig?.wordmark
  }, [settings?.tenantConfig?.wordmark])

  const tenantLogo = useMemo(() => {
    return settings?.tenantConfig?.logo
  }, [settings?.tenantConfig?.logo])

  const toPath = useMemo(() => {
    return '/c'
  }, [])

  const containerSx = useMemo(
    () => ({
      '& img': { position: 'relative !important' }
    }),
    []
  )

  return (
    <Box
      className="sidepanel-drawer-image-container"
      component={Link}
      to={toPath}
      sx={containerSx}
    >
      {!expanded && (
        <img
          className="sidepanel-drawer-image-container-logo"
          src={tenantLogo || DefaultLogo}
          alt="logo"
        />
      )}

      {expanded && (
        <img
          className="sidepanel-drawer-image-container-wordmark"
          src={tenantCombinedLogo || DefaultCombinedLogo}
          alt="wordmark"
        />
      )}
    </Box>
  )
})

// Drawer List Item component
const DrawerListItem = memo(
  ({ item, open, isMobileScreen, onNavigate, currentPath }) => {
    // ** variables
    const isActive = useMemo(() => {
      const itemPath = item.path?.toLowerCase()
      const activePath = currentPath?.toLowerCase()
      if (itemPath === '/c') {
        return activePath === '/c' || activePath === '/c/'
      }
      return activePath === itemPath || activePath?.startsWith(itemPath + '/')
    }, [item.path, currentPath])

    const buttonId = useMemo(() => item.id || '', [item.id])
    const textSx = useMemo(
      () => ({
        ...(open ? { opacity: 1 } : { opacity: 0 }),
        marginTop: 0,
        marginBottom: 0
      }),
      [open]
    )

    // ** handlers
    const handleClick = useCallback(() => {
      onNavigate(item.path)
    }, [item.path, onNavigate])

    return (
      <ListItem
        className={`sidepanel-drawer-list-item ${
          isActive ? 'active-list-item' : ''
        }`}
        disablePadding
      >
        <ListItemButton
          id={buttonId}
          className="sidepanel-drawer-list-item-button"
          onClick={handleClick}
        >
          {!isMobileScreen && (
            <Fragment>
              <Box className="curve1" />
              <Box className="curve2" />
            </Fragment>
          )}
          {item.icon && (
            <ListItemIcon className="sidepanel-drawer-list-item-icon">
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            className="sidepanel-drawer-list-item-text"
            sx={textSx}
            primary={item.title}
          />
        </ListItemButton>
      </ListItem>
    )
  }
)

// Drawer List component
const DrawerList = memo(({ items = [], open, isMobileScreen }) => {
  // ** variables
  const sideBarItemsWithIds = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: item.title.toLowerCase().split(' ').join('-')
      })),
    [items]
  )

  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = useMemo(() => location.pathname, [location.pathname])

  // ** handlers
  const handleNavigate = useCallback(
    (path) => {
      navigate(path)
    },
    [navigate]
  )

  return (
    <List className="sidepanel-drawer-list">
      {sideBarItemsWithIds.map((item) => (
        <DrawerListItem
          key={item.path || item.title}
          item={item}
          open={open}
          isMobileScreen={isMobileScreen}
          onNavigate={handleNavigate}
          currentPath={currentPath}
        />
      ))}
    </List>
  )
})

const CandidateSidepanel = () => {
  // ** states
  const theme = useTheme()
  const { settings, setSettings } = useContext(SettingsContext)
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('lg'))

  // context variables
  const { sidePanel } = settings

  // ** variables
  const navbarHeight = useMemo(() => 0, [])

  const drawerPaperSx = useMemo(
    () => (!isMobileScreen ? { top: `${navbarHeight}px` } : {}),
    [isMobileScreen, navbarHeight]
  )

  const drawerSx = useMemo(
    () => ({
      background: 'transparent',
      '.MuiPaper-root': {
        boxShadow: 'none'
      }
    }),
    []
  )

  // ** handlers
  const handleDrawerExpandToggle = useCallback(() => {
    isMobileScreen
      ? setSettings((prev) => ({
          ...prev,
          sidePanel: { ...prev.sidePanel, opened: !prev.sidePanel.opened }
        }))
      : setSettings((prev) => ({
          ...prev,
          sidePanel: { ...prev.sidePanel, expanded: !prev.sidePanel.expanded }
        }))
  }, [setSettings, isMobileScreen])

  const setMainContainerWidth = useCallback((isPanelOpen, width = 240) => {
    const mainContainer = document.querySelector(
      '.candidate-private-route-main-container-wrapper'
    )
    if (mainContainer) {
      mainContainer.style.width = !isPanelOpen
        ? '100%'
        : `calc(100% - ${width || 0}px)`
    }
  }, [])

  // ** effects
  useEffect(() => {
    if (isMobileScreen) {
      setMainContainerWidth(false)
    } else if (sidePanel?.hidden) {
      setMainContainerWidth(false)
    } else if (!sidePanel?.expanded) {
      setMainContainerWidth(true, 67)
    } else {
      setMainContainerWidth(true, 240)
    }
  }, [
    isMobileScreen,
    sidePanel?.expanded,
    sidePanel?.opened,
    sidePanel?.hidden,
    setMainContainerWidth
  ])

  return (
    !sidePanel?.hidden && (
      <Drawer
        PaperProps={{
          sx: drawerPaperSx
        }}
        className="sidepanel-drawer"
        variant={isMobileScreen ? 'temporary' : 'permanent'}
        open={isMobileScreen ? sidePanel?.opened : sidePanel?.expanded}
        onClose={handleDrawerExpandToggle}
        sx={drawerSx}
      >
        <Box
          className="sidepanel-drawer-expand-toggler-container"
          onClick={handleDrawerExpandToggle}
        >
          <ChevronLeft
            className={`sidepanel-drawer-expand-toggler-container-icon ${
              !sidePanel.expanded ? 'rotate' : ''
            }`}
          />
        </Box>
        <ImageContainer expanded={sidePanel?.expanded} />

        <DrawerList
          items={candidateItemList}
          open={sidePanel?.expanded}
          isMobileScreen={isMobileScreen}
        />
      </Drawer>
    )
  )
}

export default memo(CandidateSidepanel)
